
import { useState } from 'react';

interface DataTableProps {
  data: any[];
  hideEmployeeName?: boolean;
  isRetentionData?: boolean; // New prop to identify retention data
  retentionHeaders?: any; // Headers from A2:K2 for retention data
}

export default function DataTable({
  data,
  hideEmployeeName = false,
  isRetentionData = false,
  retentionHeaders = {},
}: DataTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <i className="ri-table-line text-4xl text-gray-400 mb-4"></i>
        <p className="text-gray-500">No data available</p>
      </div>
    );
  }

  // Get all columns from the first data item
  const allColumns = Object.keys(data[0]);

  // Filter out unwanted columns
  const filteredColumns = allColumns.filter((key) => {
    // Always exclude these system columns
    if (['id', 'is_company_total', 'created_at', 'updated_at', 'google_sheets_record_id'].includes(key)) return false;

    // Exclude raw value columns (those ending with _raw)
    if (key.endsWith('_raw')) return false;

    // Exclude total_ prefixed columns
    if (key.startsWith('total_')) return false;

    // Remove duplicate employee name columns
    if (['employee_name'].includes(key)) return false;

    // For retention data, ALWAYS exclude the name column
    if (isRetentionData && ['name', 'agent_name'].includes(key)) return false;

    // Conditionally exclude name columns if hideEmployeeName is true (for non-retention data)
    if (
      !isRetentionData &&
      hideEmployeeName &&
      ['name', 'agent_name'].includes(key)
    )
      return false;

    return true;
  });

  // Reorder columns to put Sales Agent first
  const reorderedColumns = [];
  
  // First, add sales_agent if it exists
  if (filteredColumns.includes('sales_agent')) {
    reorderedColumns.push('sales_agent');
  }
  
  // Then add all other columns except sales_agent
  filteredColumns.forEach(col => {
    if (col !== 'sales_agent') {
      reorderedColumns.push(col);
    }
  });

  const columns = reorderedColumns;

  // Separate regular employees from company totals
  const regularEmployees = data.filter(item => !item.is_company_total && item.name !== 'Total' && item.sales_agent !== 'Total');
  const companyTotals = data.filter(item => item.is_company_total);

  const filteredData = regularEmployees.filter((item) =>
    Object.values(item).some((value) =>
      String(value).toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedData = (() => {
    if (!sortConfig) return filteredData;
    const { key, direction } = sortConfig;
    return [...filteredData].sort((a, b) => {
      const aValue = a[key];
      const bValue = b[key];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  })();

  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + itemsPerPage);

  const formatValue = (value: any, key: string) => {
    // Handle #DIV/0! and other error values
    if (
      typeof value === 'string' &&
      (value.includes('#DIV/0!') ||
        value.includes('ERROR') ||
        value === 'KA FUCKED UP')
    ) {
      return '0';
    }

    if (typeof value === 'number') {
      // Handle NaN and Infinity
      if (isNaN(value) || !isFinite(value)) {
        return '0';
      }

      // For retention data, check if this is a policy-related column that should be plain numbers
      if (isRetentionData) {
        const keyLower = key.toLowerCase();
        const headerLower = getColumnHeader(key).toLowerCase();

        // Plain numeric columns (policies, dialers, roadsides) - NO DOLLAR SIGNS
        if (
          keyLower.includes('policies') ||
          keyLower.includes('dialer') ||
          keyLower.includes('roadside') ||
          headerLower.includes('policies') ||
          headerLower.includes('dialer') ||
          headerLower.includes('roadside') ||
          keyLower.includes('policy') ||
          headerLower.includes('policy')
        ) {
          return value.toLocaleString(); // plain number with commas
        }

        // Percentage columns
        if (
          (keyLower.includes('rate') && keyLower.includes('retention')) ||
          keyLower.includes('percent') ||
          keyLower.includes('%') ||
          key.endsWith('%') ||
          keyLower.includes('collection') ||
          (headerLower.includes('rate') && headerLower.includes('retention')) ||
          headerLower.includes('percent') ||
          headerLower.includes('%') ||
          headerLower.includes('collection')
        ) {
          // Format already‑percentage values
          if (value < 1 && value > 0) {
            return `${(value * 100).toFixed(1)}%`;
          } else if (value >= 1 && value <= 100) {
            return `${value}%`;
          } else {
            return `${value}%`;
          }
        }

        // Everything else gets dollar signs for retention data
        return `$${value.toLocaleString()}`;
      }

      // Non‑retention data
      if (
        key.toLowerCase().includes('revenue') ||
        key.toLowerCase().includes('price') ||
        key.toLowerCase().includes('amount') ||
        key.toLowerCase().includes('total')
      ) {
        return `$${value.toLocaleString()}`;
      }

      return value.toLocaleString();
    }

    // Handle string values that might contain percentages or dates
    if (typeof value === 'string') {
      if (isRetentionData && value.includes('%')) {
        return value; // keep original percentage formatting
      }

      // Date formatting
      if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
        try {
          return new Date(value).toLocaleDateString();
        } catch {
          return value;
        }
      }
    }

    return String(value);
  };

  const getStatusColor = (value: string) => {
    const lowerValue = String(value).toLowerCase();
    if (
      lowerValue.includes('active') ||
      lowerValue.includes('completed') ||
      lowerValue.includes('success')
    ) {
      return 'bg-green-100 text-green-800';
    }
    if (
      lowerValue.includes('pending') ||
      lowerValue.includes('processing') ||
      lowerValue.includes('waiting')
    ) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (
      lowerValue.includes('inactive') ||
      lowerValue.includes('failed') ||
      lowerValue.includes('cancelled')
    ) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  // Conditional formatting for grades
  const getGradeColor = (grade: string): string => {
    if (typeof grade !== 'string') return '';

    if (
      grade.includes('#DIV/0!') ||
      grade.includes('ERROR') ||
      grade === 'KA FUCKED UP'
    ) {
      return 'bg-red-100 text-red-800';
    }

    const gradeUpper = grade.toUpperCase().trim();
    const baseGrade = gradeUpper.charAt(0); // A, B, C, D, F

    switch (baseGrade) {
      case 'A':
        return 'bg-green-100 text-green-800';
      case 'B':
        return 'bg-blue-100 text-blue-800';
      case 'C':
        return 'bg-orange-100 text-orange-800';
      case 'D':
      case 'F':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  const isGradeColumn = (key: string, value: any): boolean => {
    if (typeof value !== 'string') return false;

    const keyLower = key.toLowerCase();
    const valueTrimmed = value.trim().toUpperCase();

    const isGradeColumnName =
      keyLower.includes('grade') ||
      keyLower.includes('rating') ||
      keyLower.includes('score');

    const isGradeValue =
      /^[A-F][+-]?$/.test(valueTrimmed) ||
      valueTrimmed === 'KA FUCKED UP' ||
      valueTrimmed.includes('#DIV/0!');

    return isGradeColumnName || isGradeValue;
  };

  // QC conditional formatting
  const getQCColor = (qcValue: string): string => {
    if (typeof qcValue !== 'string') return '';

    if (
      qcValue.includes('#DIV/0!') ||
      qcValue.includes('ERROR') ||
      qcValue === 'KA FUCKED UP'
    ) {
      return 'bg-red-100 text-red-800';
    }

    const numericMatch = qcValue.match(/(\d+(?:\.\d+)?)/);
    if (!numericMatch) return 'bg-red-100 text-red-800';

    const numericValue = parseFloat(numericMatch[1]);

    if (numericValue >= 90) {
      return 'bg-green-100 text-green-800';
    } else if (numericValue >= 80) {
      return 'bg-blue-100 text-blue-800';
    } else if (numericValue >= 70) {
      return 'bg-orange-100 text-orange-800';
    } else if (numericValue >= 60) {
      return 'bg-purple-100 text-purple-800';
    } else {
      return 'bg-red-100 text-red-800';
    }
  };

  const isQCColumn = (key: string, value: any): boolean => {
    if (typeof value !== 'string') return false;

    const keyLower = key.toLowerCase();
    const isQCColumnName = keyLower.includes('qc') || keyLower.includes('quality');

    const isQCValue =
      value.includes('%') ||
      /^\d+(\.\d+)?%?$/.test(value.trim()) ||
      value.includes('#DIV/0!');

    return isQCColumnName || (isQCColumnName && isQCValue);
  };

  // New function: Color gradient for Driven Insurance Services Overall table
  const getColumnColorGradient = (
    value: any,
    columnKey: string,
    allValues: any[]
  ): string => {
    // Only apply to non-retention data (Driven Insurance Services Overall)
    if (isRetentionData) return '';

    // Skip Sales Agent column (text field)
    if (columnKey === 'sales_agent' || columnKey === 'agent_name') return '';

    let numericValue = value;
    if (typeof value === 'string') {
      // Clean up string values to extract numbers
      const cleaned = value.replace(/[$,%\s]/g, '');
      numericValue = parseFloat(cleaned);
    }

    if (typeof numericValue !== 'number' || isNaN(numericValue) || !isFinite(numericValue)) {
      return '';
    }

    // Get all numeric values for this column from regular employees (excluding Total)
    const numericValues = allValues
      .filter(
        (item) =>
          !item.is_company_total &&
          item.sales_agent !== 'Total' &&
          item[columnKey] !== undefined &&
          item[columnKey] !== null
      )
      .map((item) => {
        const val = item[columnKey];
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          const cleaned = val.replace(/[$,%\s]/g, '');
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        }
        return null;
      })
      .filter((val) => val !== null && isFinite(val as number)) as number[];

    if (numericValues.length === 0) return '';

    // Calculate min, max, and percentiles
    const sortedValues = [...numericValues].sort((a, b) => a - b);
    const minValue = sortedValues[0];
    const maxValue = sortedValues[sortedValues.length - 1];
    
    // If all values are the same, no gradient needed
    if (minValue === maxValue) return '';

    // Calculate percentile of current value
    const percentile = (numericValue - minValue) / (maxValue - minValue);

    // Apply color gradient: Red (0-33%) -> Yellow (33-66%) -> Green (66-100%)
    if (percentile <= 0.33) {
      return 'bg-red-100 text-red-800';
    } else if (percentile <= 0.66) {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-green-100 text-green-800';
    }
  };

  // Retention color scale
  const getRetentionColorScale = (
    value: any,
    columnKey: string,
    allValues: any[]
  ): string => {
    if (!isRetentionData || !value) return '';

    let numericValue = value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[$,%\s]/g, '');
      numericValue = parseFloat(cleaned);
    }

    if (typeof numericValue !== 'number' || isNaN(numericValue)) return '';

    // Get all numeric values for this column from non-company-total rows
    const numericValues = allValues
      .filter(
        (item) =>
          !item.is_company_total &&
          item[columnKey] !== undefined &&
          item[columnKey] !== null
      )
      .map((item) => {
        const val = item[columnKey];
        if (typeof val === 'number') return val;
        if (typeof val === 'string') {
          const cleaned = val.replace(/[$,%\s]/g, '');
          const parsed = parseFloat(cleaned);
          return isNaN(parsed) ? null : parsed;
        }
        return null;
      })
      .filter((val) => val !== null && isFinite(val as number)) as number[];

    if (numericValues.length === 0) return '';

    const sortedValues = [...numericValues].sort((a, b) => a - b);
    const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)];
    const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)];

    if (numericValue <= q1) {
      return 'bg-red-100 text-red-800';
    } else if (numericValue <= q3) {
      return 'bg-yellow-100 text-yellow-800';
    } else {
      return 'bg-green-100 text-green-800';
    }
  };

  const formatColumnName = (key: string) => {
    if (key === 'sales_agent') return 'Sales Agent';
    if (key === 'agent_name') return 'Agent Name';
    if (key === 'grade') return 'Grade';
    if (key === 'qc_results') return 'QC Results';
    if (key === 'policies_sold') return 'Policies Sold';
    if (key === 'daily_app_avg') return 'Daily App Avg';
    if (key === 'bf_average') return 'BF Average';
    return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getColumnHeader = (key: string): string => {
    // For retention data, use the actual headers from A2:K2
    if (isRetentionData && retentionHeaders) {
      // If this key matches one of the retention headers, use it directly
      if (Object.values(retentionHeaders).includes(key)) {
        return key;
      }

      // For other retention data columns, use the key as is
      return key;
    }

    // For non-retention data, check if we have column mapping
    if (key.startsWith('column_') && !key.endsWith('_raw')) {
      const columnLetter = key.replace('column_', '');

      // Map column letters to their actual headers from B2:K2 (for non-retention data)
      const defaultHeaders: { [key: string]: string } = {
        B: 'Monthly Payments',
        C: 'Late Payments',
        D: 'Retention Rate',
        E: 'Amount Collected',
        F: 'Collection %',
        G: 'Avg Payment',
        H: 'Total Accounts',
        I: 'Active Accounts',
        J: 'Cancelled Accounts',
        K: 'Success Rate',
      };

      return defaultHeaders[columnLetter] || columnLetter;
    }

    // For other data types, use the existing logic
    return key
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .replace(/Id/g, 'ID');
  };

  const handleSort = (field: string) => {
    if (sortConfig && sortConfig.key === field) {
      setSortConfig({
        key: field,
        direction: sortConfig.direction === 'asc' ? 'desc' : 'asc',
      });
    } else {
      setSortConfig({ key: field, direction: 'asc' });
    }
  };

  return (
    <div>
      {/* Search Bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <i className="ri-search-line text-gray-400"></i>
          </div>
          <input
            type="text"
            placeholder="Search employee data..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>
      </div>

      {/* Table with horizontal scroll */}
      <div className="overflow-x-auto max-w-full">
        <div className="min-w-full">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  ID
                </th>
                {columns.map((column) => (
                  <th
                    key={column}
                    onClick={() => handleSort(column)}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                  >
                    <div className="flex items-center space-x-1">
                      <span>
                        {isRetentionData
                          ? getColumnHeader(column)
                          : formatColumnName(column)}
                      </span>
                      <div className="flex flex-col">
                        <i
                          className={`ri-arrow-up-s-line text-xs ${
                            sortConfig?.key === column &&
                            sortConfig.direction === 'asc'
                              ? 'text-blue-600'
                              : 'text-gray-400'
                          }`}
                        ></i>
                        <i
                          className={`ri-arrow-down-s-line text-xs -mt-1 ${
                            sortConfig?.key === column &&
                            sortConfig.direction === 'desc'
                              ? 'text-blue-600'
                              : 'text-gray-400'
                          }`}
                        ></i>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedData.map((item, index) => (
                <tr
                  key={item.id || index}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{item.id || startIndex + index + 1}
                  </td>
                  {columns.map((column) => (
                    <td key={column} className="px-6 py-4 whitespace-nowrap text-sm">
                      {/* Check for column gradient first (for Driven Insurance Services Overall) */}
                      {(() => {
                        const gradientColor = getColumnColorGradient(item[column], column, data);
                        const retentionColor = getRetentionColorScale(item[column], column, regularEmployees);
                        
                        if (gradientColor) {
                          // Apply gradient coloring for Driven Insurance Services Overall
                          return (
                            <span
                              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${gradientColor}`}
                            >
                              {formatValue(item[column], column)}
                            </span>
                          );
                        } else if (isRetentionData && retentionColor) {
                          // Apply retention coloring for retention data
                          return (
                            <span
                              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${retentionColor}`}
                            >
                              {formatValue(item[column], column)}
                            </span>
                          );
                        } else if (isGradeColumn(column, item[column])) {
                          // Grade formatting
                          return (
                            <span
                              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getGradeColor(
                                item[column]
                              )}`}
                            >
                              {formatValue(item[column], column)}
                            </span>
                          );
                        } else if (isQCColumn(column, item[column])) {
                          // QC formatting
                          return (
                            <span
                              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getQCColor(
                                item[column]
                              )}`}
                            >
                              {formatValue(item[column], column)}
                            </span>
                          );
                        } else if (column.toLowerCase().includes('status')) {
                          return (
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                item[column]
                              )}`}
                            >
                              {formatValue(item[column], column)}
                            </span>
                          );
                        } else {
                          return (
                            <span
                              className={typeof item[column] === 'number' ? 'font-medium' : ''}
                            >
                              {formatValue(item[column], column)}
                            </span>
                          );
                        }
                      })()}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Company Totals Row */}
              {isRetentionData && companyTotals.length > 0 && (
                <tr className="bg-blue-50 border-t-2 border-blue-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-900">
                    TOTAL
                  </td>
                  {columns.map((column) => (
                    <td key={column} className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className="font-bold text-blue-900 text-base">
                        {formatValue(companyTotals[0][column], column)}
                      </span>
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="text-sm text-gray-700">
            Showing {startIndex + 1} to{' '}
            {Math.min(startIndex + itemsPerPage, sortedData.length)} of{' '}
            {sortedData.length} results
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            {Array.from(
              { length: Math.min(5, totalPages) },
              (_, i) => {
                const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-1 text-sm border rounded-md transition-colors ${
                      currentPage === page
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              }
            )}
            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
